import { getDataReaderClient } from "@multiweb/clients/util";
import { ISectionLogger } from "@multiweb/logging";
import { IRowData } from "@multiweb/types/contracts";
import { IFulltextSearchCondition } from "UI/UI/UI/XGControls/Frames/FrameInterfaces";
import { IDataSourceLoadInfo, IDBDataSourceParams } from "../../../Global/Base/DataSources/Interfaces";
import { CancelError, CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { ConditionOperator, StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { EditableDBDataSource } from "./EditableDBDataSource";

/**
 * Trida reprezentujici databazovy zdroj dat pro konzumenta dat
 *
 * @todo Po přidaných nových funkcionalitách, je to třeba trochu refaktorovat je v tom dost chaos
 */

export class MSLOVDBDataSource extends EditableDBDataSource {
    protected __internalRowsMap: Map<string, IMSLOVRowData>;
    private _allPreselectReaded: boolean = false;
    private _stoppedException: boolean = false;
    private _requestId: number = 0;
    private _canceledIds: number[] = [];
    private _fullCondition: string;
    private readonly _logger: ISectionLogger;

    constructor(params: IDBDataSourceParams, logger: ISectionLogger) {
        super(params);
        this._logger = logger;
    }

    protected override get _internalRows(): Array<IMSLOVRowData> {
        return this.__internalRowsMap && Array.from(this.__internalRowsMap.values());
    }

    protected override set _internalRows(v: Array<IMSLOVRowData>) {
        this._addOrSetInternalRows(v);
    }

    public override [Symbol.iterator](): Iterator<IMSLOVRowData> {
        if (!this._internalRows) {
            this.__internalRowsMap = new Map<string, IMSLOVRowData>();
        }

        return this._internalRows[Symbol.iterator]();
    }

    protected override get _startIndex(): number {
        return super._startIndex;
    }

    protected override set _startIndex(v: number) {
        super._startIndex = v;
        if (v == 0) {
            this._intPreselectionStartIndex = 0;
        }
    }

    private _preselectionCondition: string;
    private _fullPreselectCondition: string;
    private __intPreselectionStartIndex: number = 0;

    private set _intPreselectionStartIndex(v: number) {
        this.__intPreselectionStartIndex = v;
    }

    private get _intPreselectionStartIndex(): number {
        return this.__intPreselectionStartIndex;
    }

    /**
     * Podmínka pro preselekci (seznam ID predvybranych zaznamu)
     */
    public get preselectionCondition(): string {
        return this._preselectionCondition;
    }

    public set preselectionCondition(v: string) {
        this._startIndex = 0;
        this._preselectionCondition = v;
    }

    public setConditions(preselectCond: string, fullCondition: string, filterCondition?: string, fullTextCondition?: IFulltextSearchCondition): void {
        this._preselectionCondition = preselectCond;
        this._fulltextCondition = fullTextCondition;
        this._filterCondition = filterCondition;
        this._fullPreselectCondition = StringUtils.cleanCondition(StringUtils.combineConditions(ConditionOperator.and, this._initializeCondition, this._originalCondition, this._preselectionCondition));
        this._fullCondition = StringUtils.cleanCondition(StringUtils.combineConditions(ConditionOperator.and, this._initializeCondition, this._originalCondition, fullCondition));
        this._condition = StringUtils.cleanCondition(StringUtils.combineConditions(ConditionOperator.and, filterCondition, fullTextCondition?.condition ?? "", this._initializeCondition, this._originalCondition));
    }

    public async reloadData(): Promise<void> {
        this._startIndex = 0;
        const oldBatchSize = this._batchSize;
        this._batchSize = this.__internalRowsMap ? this.__internalRowsMap.size : 50;
        this.__internalRowsMap = new Map<string, IMSLOVRowData>();
        await this._loadMsData(0, this._batchSize, true);
        this._batchSize = oldBatchSize;
    }

    public selectChangeOnData(isSelect = false, setCondition = true): void {
        if (setCondition)
            this.setConditions(`${this.dataKey} = -1`, `${this.dataKey} <> 0`); // `
        for (const pair of this.__internalRowsMap)
            pair["1"].preselect = isSelect;
    }

    protected override _cancelLoading(): void {
        super._cancelLoading();
        this._canceledIds.push(this._requestId);
    }

    private _clearInternalRows(): void {
        this.__internalRowsMap = new Map<string, IMSLOVRowData>();
    }

    protected _addOrSetInternalRows(newRowData: IMSLOVRowData[]): void {
        if (!newRowData || newRowData.length <= 0)
            return;
        if (!this.__internalRowsMap)
            this.__internalRowsMap = new Map<string, IMSLOVRowData>();

        for (const rowData of newRowData) {
            const dataKeyValue = AttrValueReader.readRow(rowData.data, this.dataKey);
            if (dataKeyValue)
                this.__internalRowsMap.set(dataKeyValue.toString(), rowData);
        }
    }

    /**
     * Changes attribute value in this._internalRows.
     * @param id Object id.
     * @param instanceID Object instanceID - sometimes the record in _internalRows is stored under it (at least in case of a new record - in MSLOV you can't add records via the "+" button, but this is an inherited function from the ancestor where it is possible).
     * @param attributeName Attribute name.
     * @param value New value.
     */
    protected override _changeRowVal(id: string, instanceID: string, attributeName: string, value: IMSLOVRowData): void {
        const row = this._internalRows.find(r => {
            return r.data.ID === id || r.data.ID === instanceID;
        });
        if (row) {
            row.data[attributeName] = value;
        }
    }

    /**
     * Nahradí záznam v this._internalRows (využívá se při rušení změn hodnot atributů)
     * @param newRow Nový záznam, kterým nahrazujeme ten původní.
     */
    protected override _replaceInternalRow(newRow: IRowData): void {
        const changedRowIndex = this._internalRows.findIndex(r => {
            return r.data.ID.toString() == newRow.ID.toString();
        });
        const rowID: string = this._internalRows[changedRowIndex]?.data?.ID?.toString();
        const originalRow = this.__internalRowsMap.get(rowID);
        originalRow.data = newRow;
    }

    /**
     * Nacte data do preselectu
     *
     * @todo Tato část je třeba předělat, aby jen vracela řádky z daty pro preselect a o zbytek se starala metoda _loadMsData, takze zde může docházet k desynchronizaci
     */
    private async _loadPreselectionData(startIndexForEvent?: number, _countForEvent: number = 5, _withoutEvent: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IMSLOVRowData[]> {
        this._stoppedException = false;

        if (this._fullPreselectCondition === `${this.dataKey} = -1`)
            return []; // `

        const startIndex = startIndexForEvent == undefined ? 0 : this._intPreselectionStartIndex;

        if (startIndex == 0) {
            this._allPreselectReaded = false;
            this._allDataRead = false;
        } else if (this._allPreselectReaded)
            return [];

        this._intPreselectionStartIndex += this._batchSize;
        const presStartIndex = this._intPreselectionStartIndex;

        try {
            this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this._className,
                this._attributes,
                this._fullPreselectCondition,
                {
                    params: this._conditionParams,
                    orderBy: this._internalOrderBy,
                    startIndex: startIndex,
                    count: this._batchSize,
                    dataLength: this._dataLength,
                    readVersioned: this._readVersioned,
                    distinct: this.useDistinct,
                    groupBy: this._groupBy,
                }, this._fullLengthAttributes, this._getRequestContext(cancellationToken));

            const loadedRows: IRowData[] = await this._runningDataLoad;
            this._allPreselectReaded = loadedRows.length < this._batchSize;

            //this._intPreselectionStartIndex -= (this._fetchCount - loadedRows.length);
            this._intPreselectionStartIndex = presStartIndex - (this._batchSize - loadedRows.length);

            const preRow = loadedRows.map((item) => {
                return { preselect: true, data: item };
            });

            if (startIndex === 0 && this._startIndex === 0)
                this._clearInternalRows();
            this._addOrSetInternalRows(preRow);

            return preRow;
        } finally {
            this._runningDataLoad = null;
        }
    }

    private async _loadMsData(startIndexForEvent?: number, countForEvent: number = 5, withoutEvent: boolean = false, keepSelectedRecords: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IMSLOVRowData[]> {
        const reqId = ++this._requestId;
        let preselectRows: IMSLOVRowData[];
        let dsLoadInfo: IDataSourceLoadInfo;
        try {
            this.loadingStarted.trigger(this, undefined);
            try {
                preselectRows = await this._loadPreselectionData(startIndexForEvent, countForEvent, withoutEvent, cancellationToken);
            } catch (err) {
                if (!(err instanceof CancelError)) {
                    throw err;
                } else
                    return [];
            }
            if (this._stoppedException)
                return [];

            if (preselectRows.length == this._batchSize) {
                startIndexForEvent = startIndexForEvent != undefined ? startIndexForEvent : 0;
                dsLoadInfo = {
                    startIndex: startIndexForEvent,
                    dataGenerator: this._getIterator.bind(this, startIndexForEvent, countForEvent),
                    lastBatch: this._allDataRead && countForEvent && ((startIndexForEvent + countForEvent) >= this.__internalRowsMap.size),
                    isFiltered: this.hasFilterCondition,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: false,
                    keepSelectedRecords: keepSelectedRecords,
                    emptyData: false,
                };

                if (!withoutEvent)
                    this.dataLoaded.trigger(this, dsLoadInfo);
                return preselectRows;
            }

            // nastaveni startIndexu musi byt tady, protoze Safari 11 ma nejake divoke akce cekani takze se mezitim co se cekalo na vyhodnoceni horniho awaitu posunula hodnota startindexu,
            // i kdyz byl pozadavek predchozi zrusen v prubehu vyhodnoceni
            if (!startIndexForEvent) {
                this._startIndex = 0;
                startIndexForEvent = 0;
            }

            const offsetFetch = this._batchSize - (this._batchSize - preselectRows.length);

            if (/@\d+/.test(this._fullCondition) && (this._conditionParams == null || this._conditionParams.length == 0)) {
                this._logger.error(`Data reader condition contains parameters but no parameter values available (${this._fullCondition})`);
                return [];
            }

            const startIndex = this._startIndex;

            if (startIndex == 0)
                this._allDataRead = false;
            else if (this._allDataRead)
                return preselectRows;

            const newStartIndex = this._batchSize - offsetFetch;
            this._startIndex += newStartIndex;
            this._runningDataLoadCTS = new CancellationTokenSource([cancellationToken]);
            this._runningDataLoad = getDataReaderClient().getDataRowsWithParams(this._className,
                this._attributes,
                this._fullCondition,
                {
                    params: this._conditionParams,
                    orderBy: this._internalOrderBy,
                    startIndex: startIndex,
                    count: this._batchSize - offsetFetch,
                    dataLength: this._dataLength,
                    readVersioned: this._readVersioned,
                    distinct: this.useDistinct,
                    groupBy: this._groupBy,
                }, this._fullLengthAttributes, this._getRequestContext(this._runningDataLoadCTS.token));

            try {
                const rows: IRowData[] = await this._runningDataLoad;
                // ten cancellationToken to tu rozbijel, bylo to cancelovane
                // TODO: Docasna uprava pred verzi (30. 1. 2016) je potreba vyresit ten cancellationToken
                if (this._canceledIds.includes(reqId)) {
                    //this._startIndex -= newStartIndex;
                    return [];
                }

                const preRow = rows.map((item) => {
                    return { preselect: false, data: item };
                });
                if (startIndex === 0 && preselectRows.length === 0 && !startIndexForEvent) {
                    this._clearInternalRows();
                }
                this._addOrSetInternalRows(preRow);

                this._allDataRead = rows.length + preselectRows.length < this._batchSize;
                //startIndexForEvent = startIndexForEvent != undefined ? startIndexForEvent : startIndex;
                const lastBatch = this._allDataRead && countForEvent && ((startIndexForEvent + countForEvent) >= this.__internalRowsMap.size);
                dsLoadInfo = {
                    startIndex: startIndexForEvent,
                    dataGenerator: this._getIterator.bind(this, startIndexForEvent, countForEvent),
                    lastBatch: lastBatch,
                    isFiltered: this.hasFilterCondition,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: false,
                    keepSelectedRecords: keepSelectedRecords,
                    emptyData: false,
                };
                if (!withoutEvent)
                    this.dataLoaded.trigger(this, dsLoadInfo);
                return preRow;
            } catch (err2) {
                if (!(err2 instanceof CancelError))
                    this.error.trigger(this, err2);
                return [];
            } finally {
                this._runningDataLoad = null;
            }
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo ?? { isFiltered: this.hasFilterCondition, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true, cancellationToken: cancellationToken });
        }
    }

    protected override *_getIterator(startIndex: number, count?: number): IterableIterator<IMSLOVRowData> {
        const internalRows = this._internalRows;
        const maxIndex = count ? Math.min(startIndex + count, internalRows.length) : internalRows.length;
        for (let i = startIndex ? startIndex : 0; i < maxIndex; i++)
            yield internalRows[i];
    }

    /**
    * Nahraje data od zacatku
    */
    public override async loadNewData(keepSelectedRecords: boolean = false, cancellationToken: CancellationToken = CancellationToken.none): Promise<IMSLOVRowData[]> {
        this._startIndex = 0;
        this._cancelLoading();
        return await this._loadMsData(undefined, undefined, undefined, keepSelectedRecords, cancellationToken);
    }

    /**
     * Vyvola dotaz na dalsi varku dat
     */
    public override getNextData(startIndex?: number, count?: number, keepSelectedRecords: boolean = false): Promise<any> {
        const inRange = (this._internalRows || []).length > (startIndex + count);
        const batchLoading = startIndex !== undefined && (inRange || this._allDataRead);

        if (!batchLoading && startIndex && startIndex > 0) {
            return this._loadMsData(startIndex, count, keepSelectedRecords);
        }

        let dsLoadInfo: IDataSourceLoadInfo;
        this.loadingStarted.trigger(this, undefined);
        try {
            if (batchLoading) {
                dsLoadInfo = {
                    startIndex: startIndex,
                    dataGenerator: this._getIterator.bind(this, startIndex, count),
                    lastBatch: /*this._allDataRead && count && */!inRange,
                    isFiltered: this.hasFilterCondition,
                    fulltextSearchString: this.fulltextCondition?.searchString,
                    refreshedData: false,
                    keepSelectedRecords: keepSelectedRecords,
                    emptyData: false,
                };
                this.dataLoaded.trigger(this, dsLoadInfo);
                return Promise.resolve();
            } else {
                return Promise.resolve([]);
            }
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo ?? { isFiltered: this.hasFilterCondition, fulltextSearchString: this.fulltextCondition?.searchString, emptyData: true });
        }
    }
    /**
     * Oznámení o změně všech načítaných atributů nově načteného objektu
     * @param _obj
     */
    protected override async _triggerObjectAttributesChanged(_obj: OWObject, _originalID?: string, _ct?: CancellationToken): Promise<void> {
        //mslov není editační a nepotřebuje načítat vazby
    }
}

export interface IMSLOVRowData {
    preselect: boolean;
    data: IRowData;
    [nope: string]: any; // pouze z důvodu, že před tím bylo na předkovi any, takže to vzalo i tohle, i kdyz na nem indexer neni definovany...
}
